import { forwardRef, type ComponentType } from 'react';
import { motion as baseMotion } from 'framer-motion';
import { isInitialPaint } from '../prerender/hydration';

/**
 * Drop-in replacement for framer-motion's `motion`: during the initial paint (server render and hydration)
 * `initial` is forced to `false`, so the HTML never contains `opacity:0` and the hydration render matches it.
 * Elements mounted later (navigation, conditional UI) keep their entrance animation.
 */
const cache = new Map<string | symbol, ComponentType<Record<string, unknown>>>();

export const motion = new Proxy(baseMotion, {
  get(target, tag: string | symbol) {
    const Base = (target as unknown as Record<string | symbol, ComponentType<Record<string, unknown>>>)[tag];
    if (typeof tag !== 'string' || !Base) return Base;
    let Wrapped = cache.get(tag);
    if (!Wrapped) {
      Wrapped = forwardRef<unknown, Record<string, unknown>>((props, ref) => (
        <Base {...props} ref={ref} initial={isInitialPaint() ? false : props.initial} />
      )) as unknown as ComponentType<Record<string, unknown>>;
      cache.set(tag, Wrapped);
    }
    return Wrapped;
  },
}) as typeof baseMotion;
